import { createContext, useEffect, useMemo, useState } from "react";
import { queryKeys } from "../react-query/constants";
import { getLoginToken, getStoredUser, setStoredUser } from "../storage";
import { getDecodedJWT, isAuthenticated } from "../utils";
import { useAuthenticatedUser } from "./hooks";
import { useQueryClient } from "@tanstack/react-query";
import { ChildProps, userProps } from "../interface";

type ThemeName = "light" | "dark";
const THEME_KEY = "app_theme";

export const AuthContext = createContext({
  user: undefined as userProps | undefined,
  token: undefined as string | undefined,
  isAuthenticated: false,
  authenticate: (token: string) => {},
  logout: () => {},
  updateUser: (data: userProps) => {},
  toggleDrawer: () => {},
  open: false,

  // ✅ theme (global)
  theme: "light" as ThemeName,
  setTheme: (_t: ThemeName) => {},
  toggleTheme: () => {},
});

function applyThemeToDom(theme: ThemeName) {
  // Apply on <html> so it affects everything
  document.documentElement.setAttribute("data-theme", theme);
}

function AuthContextProvider({ children }: ChildProps) {
  const [authToken, setAuthToken] = useState<string | undefined>(undefined);
  const [user, setUser] = useState<userProps | undefined>(undefined);
  const userDetails = useAuthenticatedUser();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  // ✅ theme state
  const [theme, setThemeState] = useState<ThemeName>("light");

  // Keep theme in sync with DOM + storage
  useEffect(() => {
    const saved =
      (localStorage.getItem(THEME_KEY) as ThemeName | null) ?? "light";
    setThemeState(saved);
    applyThemeToDom(saved);
  }, []);

  useEffect(() => {
    applyThemeToDom(theme);
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  useEffect(() => {
    if (!isAuthenticated()) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const data = getLoginToken();
    if (data) setAuthToken(data);
  }, []);

  useEffect(() => {
    const data = getStoredUser();
    if (data) setUser(data);
  }, []);

  useEffect(() => {
    if (userDetails) setUser(userDetails);
  }, [userDetails]);

  function logout() {
    setUser(undefined);
    setAuthToken(undefined);

    // ✅ preserve theme even if you clear everything else
    const savedTheme = localStorage.getItem(THEME_KEY);
    localStorage.clear();
    if (savedTheme) localStorage.setItem(THEME_KEY, savedTheme);

    queryClient.invalidateQueries([queryKeys.user]);
  }

  function updateUser(data: userProps) {
    setUser(data);
  }

  function authenticate(data: string) {
    setAuthToken(data);
    const decoded = getDecodedJWT();

    const userPropsObj: userProps = {
      _id: decoded?._id || "",
      firstname: "",
      lastname: "",
      middlename: "",
      fullname: "",
      phone: "",
      email: decoded?.email || "",
      role: decoded?.role || "",
    };

    setUser(userPropsObj);
    setStoredUser(userPropsObj);
  }

  function toggleDrawer() {
    setOpen((v) => !v);
  }

  function setTheme(t: ThemeName) {
    setThemeState(t);
  }

  function toggleTheme() {
    setThemeState((t) => (t === "light" ? "dark" : "light"));
  }

  const value = useMemo(
    () => ({
      user,
      token: authToken,
      isAuthenticated: !!authToken,
      authenticate,
      logout,
      updateUser,
      toggleDrawer,
      open,

      // ✅ theme
      theme,
      setTheme,
      toggleTheme,
    }),
    [user, authToken, open, theme],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContextProvider;
